import React from 'react';
import { FormattedMessage } from 'react-intl';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import LoadingComponent from '../components/LoadingComponent';
import UserAdminService from '../services/UserAdminService';
import messages from '../messages';
import { flattenMessages } from '../utils';

const userAdminService = new UserAdminService();

const KNOWN_ROLES = ['supplier', 'siemens', 'admin'];

// Admin-only screen (reachable via /user-manager, no nav link) to inspect and
// edit a user's authorization: their roles and their supplier scope (whitelist
// + all-suppliers grant). The backend re-checks user:manage on every call.
export default class UserManagerContainer extends React.Component {

	constructor(props) {
		super(props);
		this.state = {
			isAdmin: false,
			userIdInput: '',
			loadedUserId: '',
			roles: [],
			primarySupplier: '',
			supplierIds: [],
			supplierWildcard: false,
			newSupplierId: '',
			assignments: [],
			showLoadingModal: false,
			message: '',
			messageIsError: false
		};
	}

	componentDidMount() {
		const user = this.getUser();
		const isAdmin = !!user && user.role === 'userAdmin';
		this.setState({ isAdmin });
		if (isAdmin) {
			this.loadAssignments();
		}
	}

	getUser = () => {
		const userData = localStorage.getItem('oidc_user');
		if (!userData) {
			return null;
		}
		const user = JSON.parse(userData);
		if (user.expires_at * 1000 < Date.now()) {
			localStorage.removeItem('oidc_user');
			return null;
		}
		return user;
	};

	t = (id) => {
		const locale = localStorage.getItem('language') || 'en-US';
		const flat = flattenMessages(messages[locale] || messages['en-US']);
		return flat[id] || id;
	};

	setError = (message) => this.setState({ message, messageIsError: true });
	setInfo = (message) => this.setState({ message, messageIsError: false });

	loadAssignments = async () => {
		try {
			const result = await userAdminService.listAssignments();
			this.setState({ assignments: result.assignments || [] });
		} catch (e) {
			this.setError(this.t('userManager.errorLoadAssignments'));
		}
	};

	loadUser = async (userId) => {
		const id = (userId ?? this.state.userIdInput).trim();
		if (!id) {
			return;
		}
		this.setState({ showLoadingModal: true, message: '' });
		try {
			const roleData = await userAdminService.getRoles(id);
			const supplierData = await userAdminService.getSuppliers(id);
			this.setState({
				loadedUserId: id,
				userIdInput: id,
				roles: roleData.roles || [],
				primarySupplier: supplierData.primarySupplier || '',
				supplierIds: supplierData.supplierIds || [],
				supplierWildcard: supplierData.supplierWildcard || false,
				newSupplierId: ''
			});
			this.setInfo(this.t('userManager.loaded'));
		} catch (e) {
			this.setError(this.t('userManager.errorLoadUser'));
		} finally {
			this.setState({ showLoadingModal: false });
		}
	};

	toggleRole = (role) => {
		const roles = this.state.roles.includes(role)
			? this.state.roles.filter((r) => r !== role)
			: [...this.state.roles, role];
		this.setState({ roles });
	};

	saveRoles = async () => {
		if (!this.state.loadedUserId) {
			return;
		}
		this.setState({ showLoadingModal: true, message: '' });
		try {
			await userAdminService.setRoles(this.state.loadedUserId, this.state.roles);
			this.setInfo(this.t('userManager.rolesSaved'));
			await this.loadAssignments();
		} catch (e) {
			this.setError(this.t('userManager.errorSaveRoles'));
		} finally {
			this.setState({ showLoadingModal: false });
		}
	};

	clearRoles = async () => {
		if (!this.state.loadedUserId) {
			return;
		}
		this.setState({ showLoadingModal: true, message: '' });
		try {
			await userAdminService.clearRoles(this.state.loadedUserId);
			this.setInfo(this.t('userManager.rolesCleared'));
			await this.loadUser(this.state.loadedUserId);
			await this.loadAssignments();
		} catch (e) {
			this.setError(this.t('userManager.errorClearRoles'));
		} finally {
			this.setState({ showLoadingModal: false });
		}
	};

	addSupplierId = () => {
		const id = this.state.newSupplierId.trim();
		if (!id || this.state.supplierIds.includes(id)) {
			return;
		}
		this.setState({ supplierIds: [...this.state.supplierIds, id], newSupplierId: '' });
	};

	removeSupplierId = (id) => {
		this.setState({ supplierIds: this.state.supplierIds.filter((s) => s !== id) });
	};

	saveSuppliers = async () => {
		if (!this.state.loadedUserId) {
			return;
		}
		this.setState({ showLoadingModal: true, message: '' });
		try {
			await userAdminService.setSuppliers(
				this.state.loadedUserId,
				this.state.supplierIds,
				this.state.supplierWildcard
			);
			this.setInfo(this.t('userManager.suppliersSaved'));
			await this.loadAssignments();
		} catch (e) {
			this.setError(this.t('userManager.errorSaveSuppliers'));
		} finally {
			this.setState({ showLoadingModal: false });
		}
	};

	clearSuppliers = async () => {
		if (!this.state.loadedUserId) {
			return;
		}
		this.setState({ showLoadingModal: true, message: '' });
		try {
			await userAdminService.clearSuppliers(this.state.loadedUserId);
			this.setInfo(this.t('userManager.suppliersCleared'));
			await this.loadUser(this.state.loadedUserId);
			await this.loadAssignments();
		} catch (e) {
			this.setError(this.t('userManager.errorClearSuppliers'));
		} finally {
			this.setState({ showLoadingModal: false });
		}
	};

	render() {
		if (!this.state.isAdmin) {
			return (
				<div>
					<NavBar />
					<div className="container col-xl-6 col-lg-9 col-md-12 col-sm-12">
						<h2><FormattedMessage id="userManager.title" /></h2>
						<div className="alert alert-danger" style={{ marginTop: 20 }}>
							<FormattedMessage id="userManager.notAuthorized" />
						</div>
					</div>
					<Footer />
				</div>
			);
		}

		const hasUser = !!this.state.loadedUserId;

		return (
			<div>
				<NavBar />
				<div className="container col-xl-8 col-lg-10 col-md-12 col-sm-12">
					<h2><FormattedMessage id="userManager.title" /></h2>

					{this.state.message &&
						<div className={`alert ${this.state.messageIsError ? 'alert-danger' : 'alert-info'}`} style={{ marginTop: 10 }}>
							{this.state.message}
						</div>
					}

					<div className="card" style={{ padding: 20, marginTop: 20 }}>
						<div className="form-group">
							<label htmlFor="userIdInput"><FormattedMessage id="userManager.userId" /></label>
							<div className="form-row" style={{ alignItems: 'center' }}>
								<input
									id="userIdInput"
									type="text"
									className="form-control col-md-8"
									value={this.state.userIdInput}
									placeholder={this.t('userManager.userIdPlaceholder')}
									onChange={(e) => this.setState({ userIdInput: e.target.value })}
									onKeyDown={(e) => { if (e.key === 'Enter') { this.loadUser(); } }}
								/>
								<button type="button" className="btn btn-primary" style={{ marginLeft: 10 }} onClick={() => this.loadUser()}>
									<FormattedMessage id="userManager.load" />
								</button>
							</div>
						</div>
					</div>

					{hasUser &&
						<div className="card" style={{ padding: 20, marginTop: 20 }}>
							<h4><FormattedMessage id="userManager.rolesTitle" /> ({this.state.loadedUserId})</h4>
							<div className="form-group">
								{KNOWN_ROLES.map((role) => (
									<div className="form-check" key={role}>
										<input
											className="form-check-input"
											type="checkbox"
											id={`role-${role}`}
											checked={this.state.roles.includes(role)}
											onChange={() => this.toggleRole(role)}
										/>
										<label className="form-check-label" htmlFor={`role-${role}`}>{role}</label>
									</div>
								))}
							</div>
							<div>
								<button type="button" className="btn btn-primary" onClick={this.saveRoles}>
									<FormattedMessage id="userManager.saveRoles" />
								</button>
								{' '}
								<button type="button" className="btn btn-warning" onClick={this.clearRoles}>
									<FormattedMessage id="userManager.clearRoles" />
								</button>
							</div>
						</div>
					}

					{hasUser &&
						<div className="card" style={{ padding: 20, marginTop: 20 }}>
							<h4><FormattedMessage id="userManager.suppliersTitle" /></h4>
							<div className="form-group">
								<label><FormattedMessage id="userManager.primarySupplier" /></label>
								<input type="text" className="form-control" value={this.state.primarySupplier || ''} disabled={true} />
							</div>
							<div className="form-check" style={{ marginBottom: 10 }}>
								<input
									className="form-check-input"
									type="checkbox"
									id="supplierWildcard"
									checked={this.state.supplierWildcard}
									onChange={(e) => this.setState({ supplierWildcard: e.target.checked })}
								/>
								<label className="form-check-label" htmlFor="supplierWildcard">
									<FormattedMessage id="userManager.supplierWildcard" />
								</label>
							</div>
							<div className="form-group">
								<label><FormattedMessage id="userManager.whitelist" /></label>
								<table className="table table-sm">
									<tbody>
										{this.state.supplierIds.map((id) => (
											<tr key={id}>
												<td>{id}</td>
												<td style={{ width: '80px' }}>
													<button type="button" className="btn btn-sm btn-danger" onClick={() => this.removeSupplierId(id)}>
														<FormattedMessage id="userManager.remove" />
													</button>
												</td>
											</tr>
										))}
									</tbody>
								</table>
								<div className="form-row" style={{ alignItems: 'center' }}>
									<input
										type="text"
										className="form-control col-md-6"
										value={this.state.newSupplierId}
										placeholder={this.t('userManager.supplierIdPlaceholder')}
										onChange={(e) => this.setState({ newSupplierId: e.target.value })}
										onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); this.addSupplierId(); } }}
									/>
									<button type="button" className="btn btn-secondary" style={{ marginLeft: 10 }} onClick={this.addSupplierId}>
										<FormattedMessage id="userManager.add" />
									</button>
								</div>
							</div>
							<div>
								<button type="button" className="btn btn-primary" onClick={this.saveSuppliers}>
									<FormattedMessage id="userManager.saveSuppliers" />
								</button>
								{' '}
								<button type="button" className="btn btn-warning" onClick={this.clearSuppliers}>
									<FormattedMessage id="userManager.clearSuppliers" />
								</button>
							</div>
						</div>
					}

					<div className="card" style={{ padding: 20, marginTop: 20, marginBottom: 30 }}>
						<h4><FormattedMessage id="userManager.assignmentsTitle" /></h4>
						<table className="table table-sm">
							<thead>
								<tr>
									<th><FormattedMessage id="userManager.colUserId" /></th>
									<th><FormattedMessage id="userManager.colRoles" /></th>
									<th><FormattedMessage id="userManager.colSupplierIds" /></th>
									<th><FormattedMessage id="userManager.colWildcard" /></th>
								</tr>
							</thead>
							<tbody>
								{this.state.assignments.map((a) => (
									<tr key={a.userId} style={{ cursor: 'pointer' }} onClick={() => this.loadUser(a.userId)}>
										<td>{a.userId}</td>
										<td>{(a.roles || []).join(', ')}</td>
										<td>{(a.supplierIds || []).join(', ')}</td>
										<td>{a.supplierWildcard ? '✓' : ''}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
				<LoadingComponent hidden={this.state.showLoadingModal} />
				<Footer />
			</div>
		);
	}
}
